import axios from 'axios';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import API_URL from '../config/api';
import AuthLayout from './AuthLayout';
import './AuthLayout.css';

const EmailVerification = ({ onLogin }) => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying, success, error, expired
  const [message, setMessage] = useState('');
  const [user, setUser] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link. No token provided.');
      return;
    }

    verifyEmail(token);
  }, [searchParams]);

  const verifyEmail = async (token) => {
    try {
      const response = await axios.post(`${API_URL}/users/verify-email`, {
        token: token
      });

      setStatus('success');
      setMessage(response.data.message);
      setUser(response.data.user);
      
      // Store the JWT token
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        // Call onLogin to update the app state
        if (onLogin) {
          onLogin(response.data.user);
        }
      }
    } catch (error) {
      const errorData = error.response?.data;

      // Check if token is expired
      if (errorData?.tokenExpired) {
        setStatus('expired');
        setMessage(errorData.message);
        setUserEmail(errorData.email);
      }
      // Check if already verified
      else if (errorData?.alreadyVerified) {
        setStatus('already-verified');
        setMessage(errorData.message);
      }
      // General error
      else {
        setStatus('error');
        setMessage(errorData?.message || 'Email verification failed');
      }
    }
  };

  const handleResendVerification = async () => {
    if (!userEmail) return;

    setResending(true);
    setResendMessage('');

    try {
      const response = await axios.post(`${API_URL}/users/resend-verification`, {
        email: userEmail
      });

      setResendMessage(response.data.message);
      setStatus('resent');
    } catch (error) {
      setResendMessage(error.response?.data?.message || 'Failed to resend verification email');
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthLayout>
      <div className="register-container">
        {status === 'verifying' && (
          <div className="verification-status">
            <div className="loading-spinner"></div>
            <h1 className="verification-title">Verifying Your Email...</h1>
            <p className="verification-text">Please wait while we verify your account.</p>
          </div>
        )}

        {status === 'success' && (
          <div className="success-message">
            <div className="success-icon">✅</div>
            <h1 className="success-title">Email Verified Successfully!</h1>
            <p className="success-text">{message}</p>
            {user && (
              <div className="user-welcome">
                <p>Welcome, {user.name}! Your account is now active.</p>
                {user.role === 'respondent' && !user.profileCompleted && (
                  <p className="info-text">Complete your profile to start earning points!</p>
                )}
              </div>
            )}
            <div className="success-actions">
              {user && user.role === 'respondent' && !user.profileCompleted ? (
                <Link to="/survey-profiler/all" className="btn btn-primary btn-lg">
                  Complete Your Profile
                </Link>
              ) : (
                  <Link to="/dashboard" className="btn btn-primary btn-lg">
                    Go to Dashboard
                  </Link>
              )}
              <Link to="/" className="btn btn-outline btn-lg">
                Back to Home
              </Link>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="error-message">
            <div className="error-icon">❌</div>
            <h1 className="error-title">Verification Failed</h1>
            <p className="error-text">{message}</p>
            <div className="error-actions">
              <Link to="/auth/register" className="btn btn-primary btn-lg">
                Register Again
              </Link>
              <Link to="/auth/login" className="btn btn-outline btn-lg">
                Try Login
              </Link>
            </div>
          </div>
        )}

        {status === 'expired' && (
          <div className="error-message">
            <div className="error-icon">⏰</div>
            <h1 className="error-title">Verification Link Expired</h1>
            <p className="error-text">{message}</p>
            <p className="info-text">Don't worry! You can request a new verification email.</p>
            {resendMessage && (
              <div className={resendMessage.includes('sent') ? 'success-alert' : 'error-alert'}>
                {resendMessage}
              </div>
            )}
            <div className="error-actions">
              <button
                onClick={handleResendVerification}
                className="btn btn-primary btn-lg"
                disabled={resending}
              >
                {resending ? 'Sending...' : 'Resend Verification Email'}
              </button>
              <Link to="/auth/login" className="btn btn-outline btn-lg">
                Back to Login
              </Link>
            </div>
          </div>
        )}

        {status === 'already-verified' && (
          <div className="success-message">
            <div className="success-icon">✅</div>
            <h1 className="success-title">Already Verified</h1>
            <p className="success-text">{message}</p>
            <div className="success-actions">
              <Link to="/auth/login" className="btn btn-primary btn-lg">
                Login Now
              </Link>
            </div>
          </div>
        )}

        {status === 'resent' && (
          <div className="success-message">
            <div className="success-icon">📧</div>
            <h1 className="success-title">Email Sent!</h1>
            <p className="success-text">{resendMessage}</p>
            <p className="info-text">Please check your inbox and click the verification link.</p>
            <div className="success-actions">
              <Link to="/auth/login" className="btn btn-outline btn-lg">
                Back to Login
              </Link>
            </div>
          </div>
        )}
      </div>
    </AuthLayout>
  );
};

export default EmailVerification;